import React, { useState } from 'react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Nova Scotia', 'Northwest Territories',
  'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon',
];

const INDUSTRIES = ['Medical Devices', 'SaaS', 'Biotech', 'Fintech', 'Other'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DATE_FORMATS = [
  { value: 'YYYY-MM-DD', example: '2026-03-30' },
  { value: 'DD/MM/YYYY', example: '30/03/2026' },
  { value: 'MM/DD/YYYY', example: '03/30/2026' },
];

const CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP'];

const INITIAL = {
  legalName: 'Demo Medical Devices Inc.',
  operatingName: 'Demo Medical',
  country: 'Canada',
  province: 'Quebec',
  incorporationDate: '2018-03-12',
  businessNumber: '',
  neq: '',
  website: 'https://demomed.example.com',
  industry: 'Medical Devices',
  fiscalYearEnd: 'December',
  currency: 'CAD',
  dateFormat: 'YYYY-MM-DD',
};

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

export default function CompanySettings({ currency, setCurrency }) {
  const t = useTheme();
  const savedCompany = JSON.parse(localStorage.getItem('finkloz_company_settings') || 'null');
  const [form, setForm] = useState(savedCompany || INITIAL);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    localStorage.setItem('finkloz_company_settings', JSON.stringify(form));
    // Sync functional currency to app-level state if setCurrency is provided
    if (setCurrency && form.currency) {
      setCurrency(form.currency);
    }
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Company Profile
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Legal entity details and preferences for your organization.
      </p>

      {/* Legal Information */}
      <div style={sectionTitleStyle(t)}>Legal Information</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Company Legal Name *</label>
          <input
            type="text"
            value={form.legalName}
            onChange={e => update('legalName', e.target.value)}
            style={inputStyle(t)}
            required
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Operating Name</label>
          <input
            type="text"
            value={form.operatingName}
            onChange={e => update('operatingName', e.target.value)}
            style={inputStyle(t)}
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Jurisdiction */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Country</label>
          <select
            value={form.country}
            onChange={e => {
              update('country', e.target.value);
              if (e.target.value !== 'Canada') update('province', '');
            }}
            style={inputStyle(t)}
          >
            <option value="Canada">Canada</option>
            <option value="United States">United States</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {form.country === 'Canada' && (
          <div>
            <label style={labelStyle(t)}>Province</label>
            <select
              value={form.province}
              onChange={e => update('province', e.target.value)}
              style={inputStyle(t)}
            >
              <option value="">Select province</option>
              {PROVINCES.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Date of Incorporation</label>
          <input
            type="date"
            value={form.incorporationDate}
            onChange={e => update('incorporationDate', e.target.value)}
            style={inputStyle(t)}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Business Number / CRA</label>
          <input
            type="text"
            value={form.businessNumber}
            onChange={e => update('businessNumber', e.target.value)}
            style={inputStyle(t)}
            placeholder="123456789 RC0001"
          />
        </div>
      </div>

      {form.province === 'Quebec' && (
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle(t)}>NEQ (Quebec Enterprise Number)</label>
          <input
            type="text"
            value={form.neq}
            onChange={e => update('neq', e.target.value)}
            style={{ ...inputStyle(t), maxWidth: 300 }}
            placeholder="1234567890"
          />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Website URL</label>
          <input
            type="url"
            value={form.website}
            onChange={e => update('website', e.target.value)}
            style={inputStyle(t)}
            placeholder="https://"
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Industry</label>
          <select
            value={form.industry}
            onChange={e => update('industry', e.target.value)}
            style={inputStyle(t)}
          >
            {INDUSTRIES.map(i => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Financial Preferences */}
      <div style={{ ...sectionTitleStyle(t), marginTop: 32 }}>Financial Preferences</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Fiscal Year-End</label>
        <select
          value={form.fiscalYearEnd}
          onChange={e => update('fiscalYearEnd', e.target.value)}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          {MONTHS.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Functional Currency</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {CURRENCIES.map(c => (
            <label key={c} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="currency"
                value={c}
                checked={form.currency === c}
                onChange={() => update('currency', c)}
                style={{ accentColor: green }}
              />
              <span style={{ fontFamily: mono, fontWeight: 500 }}>{c}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle(t)}>Date Format</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {DATE_FORMATS.map(df => (
            <label key={df.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="dateFormat"
                value={df.value}
                checked={form.dateFormat === df.value}
                onChange={() => update('dateFormat', df.value)}
                style={{ accentColor: green }}
              />
              <span>{df.value}</span>
              <span style={{ fontSize: 10, color: t.textMuted, fontFamily: mono }}>({df.example})</span>
            </label>
          ))}
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
